import React, { useEffect, useState, Children } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
interface NavItemProps {
  id: string;
  label: string;
  isActive: boolean;
  onClick: (id: string) => void;
  isSubItem?: boolean;
  sectionId?: string;
}
function NavItem({
  id,
  label,
  isActive,
  onClick,
  isSubItem = false,
  sectionId
}: NavItemProps) {
  const handleClick = () => {
    onClick(id);
    if (sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  };
  return <button onClick={handleClick} className={`w-full text-left block py-2 text-sm transition-colors ${isSubItem ? 'pl-10 pr-4' : 'px-4'} ${isActive ? 'text-[#0B5FA5] border-l-4 border-[#0B5FA5] bg-blue-50/50 font-medium' : 'text-gray-600 hover:text-[#0B5FA5] hover:bg-blue-50/30 border-l-4 border-transparent'}`} data-id="element-55">
      {label}
    </button>;
}
interface ExpandableNavItemProps {
  id: string;
  label: string;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onClick: (id: string) => void;
  children: React.ReactNode;
}
function ExpandableNavItem({
  id,
  label,
  isActive,
  isExpanded,
  onToggle,
  onClick,
  children
}: ExpandableNavItemProps) {
  return <div data-id="element-56">
      <div className={`flex items-center w-full transition-colors ${isActive ? 'border-l-4 border-[#0B5FA5] bg-blue-50/50' : 'border-l-4 border-transparent hover:bg-blue-50/30'}`} data-id="element-57">
        <button onClick={() => {
        onClick(id);
        if (!isExpanded) onToggle();
      }} className={`flex-1 text-left block px-4 py-2 text-sm ${isActive ? 'text-[#0B5FA5] font-medium' : 'text-gray-600 hover:text-[#0B5FA5]'}`} data-id="element-58">
          {label}
        </button>
        <button onClick={onToggle} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-59">
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" data-id="element-60" /> : <ChevronRight className="w-3.5 h-3.5" data-id="element-61" />}
        </button>
      </div>
      {isExpanded && <div className="bg-blue-50/20" data-id="element-62">{children}</div>}
    </div>;
}
interface NavCategoryProps {
  title: string;
  children: React.ReactNode;
}
function NavCategory({
  title,
  children
}: NavCategoryProps) {
  return <div className="mt-6" data-id="element-63">
      <h3 className="px-4 text-xs font-bold text-gray-900 uppercase tracking-wider mb-2" data-id="element-64">
        {title}
      </h3>
      <div className="space-y-0.5" data-id="element-65">{children}</div>
    </div>;
}
interface ApiSidebarProps {
  activePage: string;
  onNavigate: (pageId: string) => void;
}
const sectionChildren: Record<string, string[]> = {
  'api-agreement': ['api-agreement-create', 'api-agreement-update', 'api-agreement-retrieve', 'api-agreement-delete'],
  'api-authentication': ['api-auth-initiate', 'api-auth-authenticate'],
  'api-batch': ['api-batch-submit', 'api-batch-retrieve'],
  'api-browser-payment': ['api-bp-initiate', 'api-bp-confirm', 'api-bp-retrieve'],
  'api-gateway': ['api-gateway-info', 'api-gateway-options', 'api-gateway-merchant'],
  'api-hosted-checkout': ['api-hc-create', 'api-hc-retrieve'],
  'api-payment-plan': ['api-pp-create', 'api-pp-update', 'api-pp-retrieve'],
  'api-session': ['api-session-create', 'api-session-update', 'api-session-retrieve'],
  'api-risk': ['api-risk-evaluate'],
  'api-tokenization': ['api-token-create', 'api-token-update', 'api-token-delete', 'api-token-retrieve'],
  'api-transaction': ['api-txn-pay', 'api-txn-auth', 'api-txn-capture', 'api-txn-refund', 'api-txn-void', 'api-txn-verify', 'api-txn-retrieve'],
  'api-wallet': ['api-wallet-create', 'api-wallet-update']
};
function getParentSection(pageId: string): string | null {
  for (const [parent, children] of Object.entries(sectionChildren)) {
    if (children.includes(pageId)) return parent;
  }
  return null;
}
export function ApiSidebar({
  activePage,
  onNavigate
}: ApiSidebarProps) {
  const activeParent = getParentSection(activePage);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const key of Object.keys(sectionChildren)) {
      initial[key] = key === activePage || key === activeParent;
    }
    return initial;
  });
  useEffect(() => {
    const parent = getParentSection(activePage);
    if (parent) {
      setExpandedItems(prev => ({
        ...prev,
        [parent]: true
      }));
    }
    if (sectionChildren[activePage]) {
      setExpandedItems(prev => ({
        ...prev,
        [activePage]: true
      }));
    }
  }, [activePage]);
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const isParentActive = (parentId: string) => {
    return activePage === parentId || getParentSection(activePage) === parentId;
  };
  const [searchQuery, setSearchQuery] = useState('');
  return <aside className="w-[260px] flex-shrink-0 border-r border-gray-200 bg-[#F8FAFC] flex flex-col h-screen sticky top-0 overflow-y-auto" data-id="element-66">
      <div className="p-4 sticky top-0 bg-[#F8FAFC] z-10 border-b border-gray-200" data-id="element-67">
        <div className="bg-blue-50 rounded-lg p-1 border border-blue-100 mb-3" data-id="element-68">
          <div className="bg-[#0B5FA5] shadow-sm rounded-md py-1.5 text-xs font-semibold text-white text-center" data-id="element-69">
            REST-JSON
          </div>
        </div>
        <div className="relative" data-id="element-70">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-71">
            <Search className="h-4 w-4 text-gray-400" data-id="element-72" />
          </div>
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#0B5FA5] focus:border-[#0B5FA5] sm:text-sm" placeholder="Search API docs..." data-id="element-73" />
        </div>
      </div>

      <nav className="flex-1 py-4 pb-12" data-id="element-74">
        <div className="space-y-0.5" data-id="element-75">
          <NavItem id="api-overview" label="Introduction" isActive={activePage === 'api-overview'} onClick={onNavigate} data-id="element-76" />

          <ExpandableNavItem id="api-agreement" label="Agreement" isActive={isParentActive('api-agreement')} isExpanded={!!expandedItems['api-agreement']} onToggle={() => toggleExpand('api-agreement')} onClick={onNavigate} data-id="element-77">
            <NavItem id="api-agreement" label="Create Agreement" isActive={activePage === 'api-agreement-create'} onClick={onNavigate} isSubItem sectionId="create-agreement" data-id="element-78" />
            <NavItem id="api-agreement" label="Update Agreement" isActive={activePage === 'api-agreement-update'} onClick={onNavigate} isSubItem sectionId="update-agreement" data-id="element-79" />
            <NavItem id="api-agreement" label="Retrieve Agreement" isActive={activePage === 'api-agreement-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve-agreement" data-id="element-80" />
            <NavItem id="api-agreement" label="Delete Agreement" isActive={activePage === 'api-agreement-delete'} onClick={onNavigate} isSubItem sectionId="delete-agreement" data-id="element-81" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-authentication" label="Authentication" isActive={isParentActive('api-authentication')} isExpanded={!!expandedItems['api-authentication']} onToggle={() => toggleExpand('api-authentication')} onClick={onNavigate} data-id="element-82">
            <NavItem id="api-authentication" label="Initiate Authentication" isActive={activePage === 'api-auth-initiate'} onClick={onNavigate} isSubItem sectionId="initiate-authentication" data-id="element-83" />
            <NavItem id="api-authentication" label="Authenticate Payer" isActive={activePage === 'api-auth-authenticate'} onClick={onNavigate} isSubItem sectionId="authenticate-payer" data-id="element-84" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-batch" label="Batch" isActive={isParentActive('api-batch')} isExpanded={!!expandedItems['api-batch']} onToggle={() => toggleExpand('api-batch')} onClick={onNavigate} data-id="element-85">
            <NavItem id="api-batch" label="Submit Batch" isActive={activePage === 'api-batch-submit'} onClick={onNavigate} isSubItem sectionId="submit-batch" data-id="element-86" />
            <NavItem id="api-batch" label="Retrieve Batch" isActive={activePage === 'api-batch-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve-batch" data-id="element-87" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-browser-payment" label="Browser Payment" isActive={isParentActive('api-browser-payment')} isExpanded={!!expandedItems['api-browser-payment']} onToggle={() => toggleExpand('api-browser-payment')} onClick={onNavigate} data-id="element-88">
            <NavItem id="api-browser-payment" label="Initiate Browser Payment" isActive={activePage === 'api-bp-initiate'} onClick={onNavigate} isSubItem sectionId="initiate-browser-payment" data-id="element-89" />
            <NavItem id="api-browser-payment" label="Confirm Browser Payment" isActive={activePage === 'api-bp-confirm'} onClick={onNavigate} isSubItem sectionId="confirm-browser-payment" data-id="element-90" />
            <NavItem id="api-browser-payment" label="Retrieve Browser Payment" isActive={activePage === 'api-bp-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve-browser-payment" data-id="element-91" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-gateway" label="Gateway" isActive={isParentActive('api-gateway')} isExpanded={!!expandedItems['api-gateway']} onToggle={() => toggleExpand('api-gateway')} onClick={onNavigate} data-id="element-92">
            <NavItem id="api-gateway" label="Gateway Information" isActive={activePage === 'api-gateway-info'} onClick={onNavigate} isSubItem sectionId="gateway-info" data-id="element-93" />
            <NavItem id="api-gateway" label="Payment Options Inquiry" isActive={activePage === 'api-gateway-options'} onClick={onNavigate} isSubItem sectionId="payment-options" data-id="element-94" />
            <NavItem id="api-gateway" label="Merchant Information" isActive={activePage === 'api-gateway-merchant'} onClick={onNavigate} isSubItem sectionId="merchant-info" data-id="element-95" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-hosted-checkout" label="Hosted Checkout" isActive={isParentActive('api-hosted-checkout')} isExpanded={!!expandedItems['api-hosted-checkout']} onToggle={() => toggleExpand('api-hosted-checkout')} onClick={onNavigate} data-id="element-96">
            <NavItem id="api-hosted-checkout" label="Create Checkout Session" isActive={activePage === 'api-hc-create'} onClick={onNavigate} isSubItem sectionId="create-checkout" data-id="element-97" />
            <NavItem id="api-hosted-checkout" label="Retrieve Result" isActive={activePage === 'api-hc-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve-result" data-id="element-98" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-payment-plan" label="Payment Plan" isActive={isParentActive('api-payment-plan')} isExpanded={!!expandedItems['api-payment-plan']} onToggle={() => toggleExpand('api-payment-plan')} onClick={onNavigate} data-id="element-99">
            <NavItem id="api-payment-plan" label="Create Plan" isActive={activePage === 'api-pp-create'} onClick={onNavigate} isSubItem sectionId="create-plan" data-id="element-100" />
            <NavItem id="api-payment-plan" label="Update Plan" isActive={activePage === 'api-pp-update'} onClick={onNavigate} isSubItem sectionId="update-plan" data-id="element-101" />
            <NavItem id="api-payment-plan" label="Retrieve Plan" isActive={activePage === 'api-pp-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve-plan" data-id="element-102" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-session" label="Session" isActive={isParentActive('api-session')} isExpanded={!!expandedItems['api-session']} onToggle={() => toggleExpand('api-session')} onClick={onNavigate} data-id="element-103">
            <NavItem id="api-session" label="Create Session" isActive={activePage === 'api-session-create'} onClick={onNavigate} isSubItem sectionId="create-session" data-id="element-104" />
            <NavItem id="api-session" label="Update Session" isActive={activePage === 'api-session-update'} onClick={onNavigate} isSubItem sectionId="update-session" data-id="element-105" />
            <NavItem id="api-session" label="Retrieve Session" isActive={activePage === 'api-session-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve-session" data-id="element-106" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-risk" label="Standalone Risk Assessment" isActive={isParentActive('api-risk')} isExpanded={!!expandedItems['api-risk']} onToggle={() => toggleExpand('api-risk')} onClick={onNavigate} data-id="element-107">
            <NavItem id="api-risk" label="Evaluate Risk" isActive={activePage === 'api-risk-evaluate'} onClick={onNavigate} isSubItem sectionId="evaluate-risk" data-id="element-108" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-tokenization" label="Tokenization" isActive={isParentActive('api-tokenization')} isExpanded={!!expandedItems['api-tokenization']} onToggle={() => toggleExpand('api-tokenization')} onClick={onNavigate} data-id="element-109">
            <NavItem id="api-tokenization" label="Create Token" isActive={activePage === 'api-token-create'} onClick={onNavigate} isSubItem sectionId="create-token" data-id="element-110" />
            <NavItem id="api-tokenization" label="Update Token" isActive={activePage === 'api-token-update'} onClick={onNavigate} isSubItem sectionId="update-token" data-id="element-111" />
            <NavItem id="api-tokenization" label="Delete Token" isActive={activePage === 'api-token-delete'} onClick={onNavigate} isSubItem sectionId="delete-token" data-id="element-112" />
            <NavItem id="api-tokenization" label="Retrieve Token" isActive={activePage === 'api-token-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve-token" data-id="element-113" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-transaction" label="Transaction" isActive={isParentActive('api-transaction')} isExpanded={!!expandedItems['api-transaction']} onToggle={() => toggleExpand('api-transaction')} onClick={onNavigate} data-id="element-114">
            <NavItem id="api-transaction" label="Process Payment (PAY)" isActive={activePage === 'api-txn-pay'} onClick={onNavigate} isSubItem sectionId="pay" data-id="element-115" />
            <NavItem id="api-transaction" label="Authorize Payment" isActive={activePage === 'api-txn-auth'} onClick={onNavigate} isSubItem sectionId="authorize" data-id="element-116" />
            <NavItem id="api-transaction" label="Capture Payment" isActive={activePage === 'api-txn-capture'} onClick={onNavigate} isSubItem sectionId="capture" data-id="element-117" />
            <NavItem id="api-transaction" label="Refund Payment" isActive={activePage === 'api-txn-refund'} onClick={onNavigate} isSubItem sectionId="refund" data-id="element-118" />
            <NavItem id="api-transaction" label="Void Transaction" isActive={activePage === 'api-txn-void'} onClick={onNavigate} isSubItem sectionId="void" data-id="element-119" />
            <NavItem id="api-transaction" label="Verify Card" isActive={activePage === 'api-txn-verify'} onClick={onNavigate} isSubItem sectionId="verify" data-id="element-120" />
            <NavItem id="api-transaction" label="Retrieve Order" isActive={activePage === 'api-txn-retrieve'} onClick={onNavigate} isSubItem sectionId="retrieve" data-id="element-121" />
          </ExpandableNavItem>

          <ExpandableNavItem id="api-wallet" label="Wallet" isActive={isParentActive('api-wallet')} isExpanded={!!expandedItems['api-wallet']} onToggle={() => toggleExpand('api-wallet')} onClick={onNavigate} data-id="element-122">
            <NavItem id="api-wallet" label="Create Wallet Payment" isActive={activePage === 'api-wallet-create'} onClick={onNavigate} isSubItem sectionId="create-wallet" data-id="element-123" />
            <NavItem id="api-wallet" label="Update Wallet" isActive={activePage === 'api-wallet-update'} onClick={onNavigate} isSubItem sectionId="update-wallet" data-id="element-124" />
          </ExpandableNavItem>
        </div>

        <NavCategory title="SDKs" data-id="element-125">
          <NavItem id="api-sdk-checkout" label="Checkout" isActive={activePage === 'api-sdk-checkout'} onClick={onNavigate} data-id="element-126" />
          <NavItem id="api-sdk-clicktopay" label="Click to Pay" isActive={activePage === 'api-sdk-clicktopay'} onClick={onNavigate} data-id="element-127" />
          <NavItem id="api-sdk-paypal" label="Paypal" isActive={activePage === 'api-sdk-paypal'} onClick={onNavigate} data-id="element-128" />
          <NavItem id="api-sdk-risk" label="Risk" isActive={activePage === 'api-sdk-risk'} onClick={onNavigate} data-id="element-129" />
          <NavItem id="api-sdk-session" label="Session" isActive={activePage === 'api-sdk-session'} onClick={onNavigate} data-id="element-130" />
        </NavCategory>
      </nav>
    </aside>;
}